import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Bar, Doughnut, Line } from 'react-chartjs-2'
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
} from 'chart.js'
import DashboardService from 'Main/service/DashboardService'
import { MetricCard } from 'views/components/Card/MetricCard'
import { formatNumber } from 'chart.js/helpers'
import { formatCurrency } from 'Main/Utils'
import { formatDateTime } from 'Main/Utils'
import { statusTone } from 'Main/Utils'
import { TYPE_COLORS } from 'Main/Constant'
import { DashboardSkeleton } from './Schelethon/DashboardSkeleton'
import { STATUS_COLORS } from 'Main/Constant'
import { dateRangeForDays } from 'Main/Utils'
import { companyName } from 'version'

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
)

export default function DashboardPage () {
  const navigate = useNavigate()
  const dashboardService = useMemo(() => new DashboardService(), [])
  const initialRange = useMemo(() => dateRangeForDays(30), [])
  const [from, setFrom] = useState(initialRange.from)
  const [to, setTo] = useState(initialRange.to)
  const [preset, setPreset] = useState(30)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [stats, setStats] = useState(null)

  const fetchStats = useCallback(async ({ background = false } = {}) => {
    try {
      background ? setRefreshing(true) : setLoading(true)
      setError('')
      const response = await dashboardService.getStats({ from, to })
      setStats(response?.data || {})
    } catch (requestError) {
      if ([401, 403].includes(requestError?.response?.status)) {
        navigate('/auth/login', { replace: true })
        return
      }
      setError(requestError?.response?.data?.message || 'The payment overview could not be loaded.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [dashboardService, from, navigate, to])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const applyPreset = (days) => {
    const range = dateRangeForDays(days)
    setPreset(days)
    setFrom(range.from)
    setTo(range.to)
  }

  const overview = stats?.overview || {}
  const byStatus = useMemo(() => stats?.byStatus || [], [stats])
  const byType = useMemo(() => stats?.byType || [], [stats])
  const trend = useMemo(() => stats?.activityTrend || [], [stats])
  const recent = stats?.recent || []
  const topEndpoints = stats?.topEndpoints || []

  const trendData = useMemo(() => ({
    labels: trend.map((item) => new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: 'short' }).format(new Date(item.date))),
    datasets: [
      {
        label: 'Callback Pembayaran',
        data: trend.map((item) => Number(item.count || 0)),
        borderColor: '#4fd1c5',
        backgroundColor: '#4fd1c5',
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 4,
        yAxisID: 'count'
      }
    ]
  }), [trend])

  const statusData = useMemo(() => ({
    labels: byStatus.map((item) => item.status_code || 'Unknown'),
    datasets: [{
      data: byStatus.map((item) => Number(item.count || 0)),
      backgroundColor: STATUS_COLORS,
      borderWidth: 0,
      spacing: 3
    }]
  }), [byStatus])

  const typeData = useMemo(() => ({
    labels: byType.map((item) => item.type || 'Unknown'),
    datasets: [{
      label: 'Callbacks',
      data: byType.map((item) => Number(item.count || 0)),
      backgroundColor: TYPE_COLORS,
      borderRadius: 7,
      borderSkipped: false
    }]
  }), [byType])

  const chartTextColor = '#8f9bb3'
  const chartGridColor = 'rgba(143, 155, 179, 0.12)'
  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top', align: 'end', labels: { color: chartTextColor, usePointStyle: true, boxWidth: 7 } },
      tooltip: {
        callbacks: {
          label: (context) => context.dataset.yAxisID === 'amount'
            ? ` Volume: ${formatCurrency(context.raw)}`
            : ` Callbacks: ${formatNumber(context.raw)}`
        }
      }
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: chartTextColor, maxTicksLimit: 8 } },
      amount: {
        position: 'left',
        beginAtZero: true,
        grid: { color: chartGridColor },
        ticks: { color: chartTextColor, callback: (value) => formatCurrency(value, true) }
      },
      count: {
        position: 'right',
        beginAtZero: true,
        grid: { display: false },
        ticks: { color: chartTextColor, precision: 0 }
      }
    }
  }

  const statusOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '72%',
    plugins: {
      legend: { position: 'bottom', labels: { color: chartTextColor, usePointStyle: true, boxWidth: 7, padding: 18 } }
    }
  }

  const typeOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, grid: { color: chartGridColor }, ticks: { color: chartTextColor, precision: 0 } },
      y: { grid: { display: false }, ticks: { color: chartTextColor } }
    }
  }

  if (loading) return <DashboardSkeleton />

  return (
    <div className="gateway-dashboard">
      <section className="gateway-hero">
        <div>
          <h2>{companyName}</h2>
        </div>
        <div className="gateway-controls">
          <div className="gateway-presets" aria-label="Dashboard period">
            {[7, 30, 90].map((days) => (
              <button key={days} type="button" className={preset === days ? 'active' : ''} onClick={() => applyPreset(days)}>
                {days}D
              </button>
            ))}
          </div>
          <label className="gateway-date-field">
            <input type="date" value={from} max={to} onChange={(event) => { setPreset(null); setFrom(event.target.value) }} />
          </label>
          <label className="gateway-date-field">
            <input type="date" value={to} min={from} onChange={(event) => { setPreset(null); setTo(event.target.value) }} />
          </label>
          <button type="button" className="gateway-refresh" onClick={() => fetchStats({ background: true })} disabled={refreshing}>
            <i className={`fa-solid fa-rotate${refreshing ? ' fa-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </section>

      {error && (
        <div className="gateway-error" role="alert">
          <i className="fa-solid fa-triangle-exclamation" />
          <span>{error}</span>
          <button type="button" onClick={() => fetchStats()}>Try again</button>
        </div>
      )}

      <section className="gateway-stat-grid" aria-label="Payment summary">
        {/* <MetricCard
          label="Processed volume"
          value={formatCurrency(overview.totalVolume, true)}
          detail={`${formatCurrency(overview.successfulVolume, true)} delivered successfully`}
          icon="fa-money-bill-transfer"
        /> */}
        <MetricCard
          label="Success rate"
          value={`${Number(overview.successRate || 0).toFixed(1)}%`}
          detail={`${formatNumber(overview.successfulCallbacks)} successful callbacks`}
          icon="fa-circle-check"
          tone="success"
        />
        <MetricCard
          label="Callback traffic"
          value={formatNumber(overview.totalCallbacks)}
          detail={`${formatNumber(overview.failedCallbacks)} failed · ${formatNumber(overview.pendingCallbacks)} pending`}
          icon="fa-satellite-dish"
          tone={overview.failedCallbacks ? 'warning' : 'primary'}
        />
        <MetricCard
          label="Active channels"
          value={`${formatNumber(overview.activeVendors)}/${formatNumber(overview.totalVendors)}`}
          detail={`${formatNumber(overview.activeQrisConfigs)} QRIS · ${formatNumber(overview.activeCallbackConfigs)} callback routes`}
          icon="fa-building-columns"
          tone="info"
        />
      </section>

      <section className="gateway-chart-grid">
        <article className="gateway-panel gateway-trend-panel">
          <div className="gateway-panel-head">
            <div>
              <span className="gateway-panel-kicker">TRANSACTION FLOW</span>
              <h3>Volume Callback Pembayaran</h3>
            </div>
          </div>
          <div className="gateway-chart gateway-chart-large">
            {trend.length ? <Line data={trendData} options={trendOptions} /> : <div className="gateway-empty">No activity in this period</div>}
          </div>
        </article>

        <article className="gateway-panel gateway-status-panel">
          <div className="gateway-panel-head">
            <div>
              <span className="gateway-panel-kicker">DELIVERY HEALTH</span>
              <h3>Callback status</h3>
            </div>
          </div>
          <div className="gateway-chart gateway-chart-donut">
            {byStatus.length ? <Doughnut data={statusData} options={statusOptions} /> : <div className="gateway-empty">No status data</div>}
            {byStatus.length > 0 && (
              <div className="gateway-donut-center">
                <strong>{formatNumber(overview.totalCallbacks)}</strong>
                <span>callbacks</span>
              </div>
            )}
          </div>
        </article>
      </section>

      <section className="gateway-lower-grid">
        <article className="gateway-panel">
          <div className="gateway-panel-head">
            <div>
              <span className="gateway-panel-kicker">CHANNEL MIX</span>
              <h3>Callbacks by payment type</h3>
            </div>
          </div>
          <div className="gateway-chart gateway-chart-medium">
            {byType.length ? <Bar data={typeData} options={typeOptions} /> : <div className="gateway-empty">No channel data</div>}
          </div>
        </article>

        <article className="gateway-panel">
          <div className="gateway-panel-head">
            <div>
              <span className="gateway-panel-kicker">ROUTE LOAD</span>
              <h3>Top callback endpoints</h3>
            </div>
            <Link to="/dashboard/global-callback-logs">View logs <i className="fa-solid fa-arrow-right" /></Link>
          </div>
          <div className="gateway-endpoint-list">
            {topEndpoints.length ? topEndpoints.map((endpoint, index) => {
              const maximum = Number(topEndpoints[0]?.count || 1)
              const percent = Math.max((Number(endpoint.count || 0) / maximum) * 100, 3)
              return (
                <div className="gateway-endpoint" key={`${endpoint.callback_url}-${index}`}>
                  <div>
                    <code title={endpoint.callback_url}>{endpoint.callback_url || 'Unknown endpoint'}</code>
                    <strong>{formatNumber(endpoint.count)}</strong>
                  </div>
                  <span><i style={{ width: `${percent}%` }} /></span>
                </div>
              )
            }) : <div className="gateway-empty">No endpoint activity</div>}
          </div>
        </article>
      </section>

      <section className="gateway-panel gateway-activity-panel">
        <div className="gateway-panel-head">
          <div>
            <span className="gateway-panel-kicker">LATEST EVENTS</span>
            <h3>Recent callback activity</h3>
          </div>
          <div className="gateway-generated">Updated {formatDateTime(stats?.generatedAt)}</div>
        </div>
        <div className="gateway-table-wrap">
          <table className="gateway-table">
            <thead>
              <tr><th>Payment type</th><th>Callback endpoint</th>
              {/* <th>Amount</th> */}
              <th>Status</th><th>Received</th></tr>
            </thead>
            <tbody>
              {recent.length ? recent.map((row) => (
                <tr key={row.id}>
                  <td><span className="gateway-type-icon">{String(row.type || '?').slice(0, 2).toUpperCase()}</span>{row.type || 'Unknown'}</td>
                  <td><code title={row.callback_url}>{row.callback_url || '—'}</code></td>
                  {/* <td>{formatCurrency(row.amount)}</td> */}
                  <td><span className={`status-${statusTone(row.status_code)}`}><i />{row.status_code || 'Unknown'}</span></td>
                  <td>{formatDateTime(row.creation)}</td>
                </tr>
              )) : (
                <tr><td colSpan="5"><div className="gateway-empty">No callback activity in this period</div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
